import React, { useId } from 'react';
import { Input } from './input';
import { Label } from './label';
import { cn } from '@/lib/utils';

interface InputFieldProps extends Omit<React.ComponentProps<typeof Input>, 'error'> {
  label?: string;
  error?: string | null;
  touched?: boolean;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>(
  ({ label, error, touched, helperText, leftIcon, rightIcon, className, id, ...props }, ref) => {
    const generatedId = useId();
    const inputId = id ?? `input-${generatedId}`;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;
    const hasError = Boolean(touched && error);
    const describedBy = [
      props['aria-describedby'],
      hasError ? errorId : helperText ? helperId : undefined,
    ].filter(Boolean).join(' ') || undefined;
    
    return (
      <div className="space-y-2">
        {label && (
          <Label htmlFor={inputId} className="text-sm font-medium">
            {label}
            {props.required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        
        <div className="relative">
          {leftIcon && (
            <div aria-hidden="true" className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
              {leftIcon}
            </div>
          )}
          
          <Input
            ref={ref}
            {...props}
            id={inputId}
            aria-invalid={hasError || props['aria-invalid'] || undefined}
            aria-describedby={describedBy}
            className={cn(
              leftIcon && "pl-10",
              rightIcon && "pr-10",
              hasError && "border-destructive focus-visible:ring-destructive",
              className
            )}
          />
          
          {rightIcon && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
              {rightIcon}
            </div>
          )}
        </div>
        
        {hasError && (
          <p id={errorId} role="alert" aria-live="polite" className="text-sm text-destructive">{error}</p>
        )}
        
        {helperText && !hasError && (
          <p id={helperId} className="text-sm text-muted-foreground">{helperText}</p>
        )}
      </div>
    );
  }
);

InputField.displayName = "InputField";
